/**
 * Pre-Session Reflection API Service (MED-803 / E3.1).
 *
 * Participant-facing endpoints backing the pre-session reflection page.
 * All routes gated by the `live_session_delivery` feature flag at the API.
 */

import apiClient from './client.js';

// ============================================================================
// Types — mirror medley-api/types/sessionReflection.ts
// ============================================================================

export type ReflectionStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED';

export type SessionFormat = 'Virtual' | 'In-Person';

export interface ReflectionGroupMember {
  user_id: string;
  first_name: string;
  // MED-1162 — preferred name when set; the card labels by preferred-or-first.
  preferred_name: string | null;
  last_initial: string;
  is_current_participant: boolean;
}

export interface ReflectionGroupContext {
  group_id: string | null;
  group_name: string | null;
  member_count: number;
  members: ReflectionGroupMember[];
}

export interface ReflectionLeader {
  user_id: string | null;
  display_name: string;
  role_label: 'COACH' | 'PEER LEADER' | null;
}

export interface ReflectionSessionDetails {
  topic_label: string | null;
  session_number: number | null;
  total_sessions: number | null;
  scheduled_start: string | null;
  scheduled_end: string | null;
  duration_minutes: number | null;
  timezone: string;
  format: SessionFormat;
  location: string | null;
  meeting_link: string | null;
  meeting_dial_in: string | null;
  leader: ReflectionLeader;
}

export interface ReflectionPageHeader {
  session_title: string;
  /**
   * MED-1146 — display-aware resolved topic (MED-1103; already on the payload).
   * Null when no topic is set. The page composes the `Session N:` label from
   * this + session_number + group name; it does NOT branch on LSD (the label is
   * driven by topic presence — MED-1146 Decision #3).
   */
  session_topic: string | null;
  program_name: string | null;
  organization_name: string | null;
  duration_minutes: number | null;
  scheduled_start: string | null;
  timing_badge: string;
}

export interface ReflectionQuestionWithResponse {
  id: string;
  session_id: string;
  prompt: string;
  placeholder_text: string | null;
  sort_order: number;
  response_text: string;
  response_id: string | null;
}

// ---------------------------------------------------------------------------
// Peer-leader banner (MED-813 / E4.1) — mirrors medley-api PeerLeaderBlock
// ---------------------------------------------------------------------------

/** Banner States 1–7 respectively. */
export type PeerLeaderState =
  | 'confirmed_leader' // State 1 — "You're leading"
  | 'backup_needed' // State 2 — "Can you lead today?"
  | 'volunteer_pending' // State 3 — you volunteered, awaiting CM
  | 'another_assigned' // State 4 — "[Name] stepped in to lead"
  | 'backup_assigned' // State 5 — "You've got this"
  | 'declined' // State 6 — you declined
  | 'leader_declined_self'; // State 7 (MED-1168) — you're still the assigned leader but declined; offer self re-confirm

export interface PeerLeaderBlock {
  state: PeerLeaderState;
  leader_name: string | null;
  participant_first_name: string;
  session_number: number | null;
  session_time_label: string | null;
  cm_name: string | null;
  ai_assist_bullet: boolean;
  about_this_session: string | null;
}

export type VolunteerResponse = 'volunteered' | 'declined';

export interface VolunteerResultPayload {
  peer_leader: PeerLeaderBlock | null;
  cohortId: string;
}

/**
 * MED-937 / E3.1b — "Reflect on Prior Commitments" payload block.
 * Non-null only when the cohort has commitment tracking enabled AND the
 * participant has a non-empty commitment from the immediately preceding
 * session in the same group. Null → the section is omitted entirely.
 */
export interface PriorCommitmentBlock {
  commitment_id: string;
  commitment_text: string;
  /** Saved reflection text; empty string when no row exists yet. */
  reflection_text: string;
}

/**
 * MED-984 / E3.1c — Per-rule breakdown of why the session content is
 * considered incomplete. Mirrors the AuthoredAgendaIncompleteReasons shape
 * the coach-prep surface uses. Optional on the page — diagnostics only.
 */
export interface PreSessionIncompleteReasons {
  sections_missing_required_fields: boolean;
}

export interface PreSessionReflectionPayload {
  session_id: string;
  cohort_id: string;
  // MED-1180 — per-cohort LSD gate. Client defense-in-depth: render
  // FeaturesDisabledPage when false (backend also hard-404s the route).
  live_session_delivery_enabled: boolean;
  header: ReflectionPageHeader;
  /**
   * MED-1101 — resolved session-number description
   * (`session_number_config.description`) for the "Session Description" block
   * (AC6/AC7/AC8). Null when unset → block hidden (AC9).
   */
  description: string | null;
  ai_features_enabled: boolean;
  /** MED-876 — per-session AI Listening toggle. AI banner gates on BOTH flags. */
  ai_listening_enabled: boolean;
  status: ReflectionStatus;
  question_count: number;
  estimated_minutes: number;
  questions: ReflectionQuestionWithResponse[];
  session_details: ReflectionSessionDetails;
  group: ReflectionGroupContext;
  is_locked: boolean;
  /** MED-1127 — session is live now (status 'in_progress'). Drives the top "Live now" banner. */
  is_live: boolean;
  /** MED-1127 — session is in the T-30 pre-start window. Drives the top "Starting Soon" banner. */
  starting_soon: boolean;
  /**
   * MED-984 / E3.1c — True when the session's AGENDA has not yet met the
   * minimum bar (≥1 section, each with name + duration). When true, the page
   * renders a gray "not yet ready" Alert and withholds the reflection form,
   * reading_tasks, and prior_commitment. MED-1182 — no longer considers
   * reflection prompts.
   */
  is_incomplete: boolean;
  /** MED-984 — per-rule breakdown when is_incomplete is true; null otherwise. */
  incomplete_reasons: PreSessionIncompleteReasons | null;
  /**
   * MED-1182 (E1.3f-R1) — the "not blank" signal. True when the session has
   * any pre-session work (≥1 reflection prompt OR reading/tasks OR commitment
   * review enabled). When false AND is_incomplete is also false, the session
   * is BLANK: render a logistics-only view (no reflection form, no AI banner).
   */
  pre_session_work_present: boolean;
  /**
   * MED-984 / E3.1c — CM-authored Reading / Tasks content (sanitized HTML)
   * for the session. Null when no authored content exists OR the authored
   * value is empty — the page omits the section (AC-RT2).
   */
  reading_tasks: string | null;
  /**
   * MED-1115 — when the participant marked the Reading / Tasks section reviewed
   * (ISO timestamp), or null if not yet reviewed. Drives the card's "Reviewed"
   * header check + collapse-on-return. Always null when reading_tasks is null.
   */
  reading_tasks_reviewed_at: string | null;
  /** MED-813 — peer-leader banner state, or null for no banner. */
  peer_leader: PeerLeaderBlock | null;
  /** MED-937 — prior-session commitment + saved reflection; null hides section. */
  prior_commitment: PriorCommitmentBlock | null;
  /**
   * MED-1226 (AC5) — true when the Commitment Review section is configured but
   * its prior-session data doesn't exist yet because the prior session isn't
   * completed (reachable now that trigger (b) can open prep early). Mutually
   * exclusive with a non-null `prior_commitment`. The peer-leader prep accordion
   * shows an "available once the prior session is completed" placeholder row
   * instead of hiding the section.
   */
  prior_commitment_pending: boolean;
  /**
   * MED-1062 (E4.0h) — true when the session is a no-show 'missed' peer-led
   * session pending reschedule. The page renders a full "Session pending
   * reschedule" state and withholds the reflection form + peer-leader banner.
   */
  is_missed: boolean;
}

export interface UpsertResponsePayload {
  status: ReflectionStatus;
  response: {
    id: string;
    session_id: string;
    participant_id: string;
    question_id: string;
    response_text: string;
  };
}

export interface BulkSavePayload {
  status: ReflectionStatus;
  responses: Array<{
    id: string;
    session_id: string;
    participant_id: string;
    question_id: string;
    response_text: string;
  }>;
}

// ============================================================================
// API methods
// ============================================================================

export const preSessionReflectionApi = {
  get: (sessionId: string): Promise<PreSessionReflectionPayload> =>
    apiClient.get<PreSessionReflectionPayload>(
      `/api/participant/sessions/${sessionId}/reflection`
    ),

  upsertResponse: (
    sessionId: string,
    questionId: string,
    responseText: string
  ): Promise<UpsertResponsePayload> =>
    apiClient.put<UpsertResponsePayload>(
      `/api/participant/sessions/${sessionId}/reflection/responses`,
      { question_id: questionId, response_text: responseText }
    ),

  bulkSave: (
    sessionId: string,
    responses: Array<{ question_id: string; response_text: string }>
  ): Promise<BulkSavePayload> =>
    apiClient.post<BulkSavePayload>(
      `/api/participant/sessions/${sessionId}/reflection/save`,
      { responses }
    ),

  /** MED-813 — record a peer-leader volunteer response; returns recomputed banner. */
  volunteer: (
    sessionId: string,
    response: VolunteerResponse
  ): Promise<VolunteerResultPayload> =>
    apiClient.post<VolunteerResultPayload>(
      `/api/participant/sessions/${sessionId}/volunteer`,
      { response }
    ),

  /**
   * MED-1168 — the assigned session leader re-confirms their own RSVP in place.
   * Hits the existing MED-905 leader-RSVP endpoint (which already authorizes on
   * `session_leader_id === caller`), flipping a stale 'declined' back to
   * 'confirmed' with no CM round-trip. The caller refetches the reflection
   * payload afterward, so the recomputed peer_leader state (confirmed_leader /
   * backup_assigned) drives the redirect to the prep / Start Session page.
   */
  confirmLead: (
    sessionId: string
  ): Promise<{
    session_id: string;
    session_leader_rsvp_status: string;
    session_leader_rsvp_responded_at: string;
  }> =>
    apiClient.post(`/api/sessions/${sessionId}/peer-leader-rsvp`, {
      status: 'confirmed',
    }),

  /**
   * MED-937 — autosave the participant's reflection on their prior
   * commitment. The commitment_id is resolved server-side from the (cohort,
   * group, session_number − 1, participant) tuple.
   */
  upsertPriorCommitmentReflection: (
    sessionId: string,
    reflectionText: string
  ): Promise<PriorCommitmentBlock> =>
    apiClient.put<PriorCommitmentBlock>(
      `/api/participant/sessions/${sessionId}/reflection/prior-commitment`,
      { reflection_text: reflectionText }
    ),

  /**
   * MED-1115 — set/clear the participant's "Reading / Tasks reviewed" marker.
   * Returns the resolved marker so the card can update its reviewed/collapsed
   * state without a refetch.
   */
  setReadingTasksReviewed: (
    sessionId: string,
    reviewed: boolean
  ): Promise<{ reading_tasks_reviewed_at: string | null }> =>
    apiClient.put<{ reading_tasks_reviewed_at: string | null }>(
      `/api/participant/sessions/${sessionId}/reflection/reading-tasks-reviewed`,
      { reviewed }
    ),
};
